import type { AIAnswer, AIAskView, AIProviderSummary, AISummary, AIVerdict } from "../api/types";

/**
 * Разбор ответов ИИ-судьи для панелей (ADR-0038).
 *
 * Ни сети, ни React: всё, что здесь, проверяется тестом без браузера. Сервер
 * шлёт сырые числа и статусы; слова, тона и доли собираются тут, чтобы
 * панель рынка и сводка сравнения читали один и тот же ответ одинаково.
 */

/** Имя провайдера для человека. */
export function providerLabel(p: string): string {
  switch (p) {
    case "typesafe":
      return "TypeSafe Jev";
    case "openai":
      return "OpenAI Decisions";
    default:
      return p;
  }
}

/** Сколько ждать до отказа опроса: дольше этого запрос сам станет no_response. */
export const AI_POLL_LIMIT_MS = 30_000;

/** Опрашивать ли запрос дальше. */
export function keepPolling(v: AIAskView | undefined, startedAt: number, now: number): boolean {
  if (now - startedAt > AI_POLL_LIMIT_MS) return false;
  return !v || !v.terminal;
}

/** Состояние запроса словами. */
export function statusWord(v: AIAskView): string {
  switch (v.view_status) {
    case "pending":
      return "трейдер собирает снимок…";
    case "answering":
      return "провайдеры отвечают…";
    case "done":
      return "ответили все";
    case "no_response":
      return "трейдер не ответил: судья не поднят или трейдер перезапускался";
    case "failed":
      return `запрос не отправлен: ${v.error ?? "шина недоступна"}`;
    case "expired":
      return v.snapshot?.reason ?? "запрос дошёл до трейдера слишком поздно";
    case "rejected":
      return v.snapshot?.reason ?? "спрашивать было не о чем";
  }
}

/** Действие словами. */
export function actionLabel(a: AIVerdict["action"]): string {
  switch (a) {
    case "buy_up":
      return "купить Up";
    case "buy_down":
      return "купить Down";
    case "skip":
      return "не ставить";
    default:
      return "—";
  }
}

/** Главный вердикт строки: что сказал провайдер, одной фразой. */
export function verdictWord(v: AIVerdict): string {
  switch (v.status) {
    case "error":
      return `ошибка${v.http_status ? ` HTTP ${v.http_status}` : ""}`;
    case "timeout":
      return "не уложился в срок";
    case "interrupted":
      return "прерван рестартом трейдера";
    case "unknown_shape":
      return "ответ не разобран";
    default:
      break;
  }
  if (v.entered) return `СТАВИТЬ: ${actionLabel(v.action)}`;
  if (v.action === "skip") return "НЕ СТАВИТЬ";
  if (v.action) return `не ставить: ${actionLabel(v.action)} ниже порога уверенности`;
  return v.status === "refusal" ? "отказ" : "—";
}

/** Вопросы, у которых есть свои поля карточки: в список прочих они не входят. */
const CORE_QUESTIONS = new Set(["p_up", "action"]);

/**
 * Прочие ответы провайдера словами: вопросы владельца сверх обязательных
 * (phase, pull…). Порядок — как в файле вопросов не известен клиенту, поэтому
 * по имени, чтобы две карточки читались одинаково.
 */
export function extraAnswers(v: AIVerdict): { name: string; text: string }[] {
  if (!v.answers) return [];
  return Object.keys(v.answers)
    .filter((n) => !CORE_QUESTIONS.has(n))
    .sort()
    .map((name) => ({ name, text: answerWord(v.answers![name]!) }));
}

/** Один ответ словами. */
export function answerWord(a: AIAnswer): string {
  if (a.unknown) return "не разобран";
  if (a.refused) return a.refusal ? `отказ: ${a.refusal}` : "отказ";
  switch (a.kind) {
    case "predicate":
      return pct(a.p);
    case "choice":
      return `${a.choice ?? "—"} · ${pct(a.confidence, 0)}`;
    case "score":
      return `${a.score === undefined ? "—" : a.score.toFixed(2)} · ${pct(a.confidence, 0)}`;
  }
}

/** Тон вердикта для цвета. */
export function verdictTone(v: AIVerdict): "good" | "bad" | "muted" | "warn" {
  if (v.status === "error" || v.status === "timeout" || v.status === "unknown_shape") return "bad";
  if (v.status === "interrupted" || v.status === "refusal") return "warn";
  if (v.entered) return "good";
  return "muted";
}

/**
 * Противоречие между ответами провайдера: велит купить Up, а вероятность Up
 * ниже половины (или наоборот). Флаг, а не ошибка: модель вправе считать, что
 * дешёвая сторона стоит своей цены, но такой ответ стоит прочесть глазами.
 */
export function inconsistent(v: AIVerdict): boolean {
  if (v.p_up === undefined || v.p_up === null) return false;
  return (v.action === "buy_up" && v.p_up < 0.5) || (v.action === "buy_down" && v.p_up > 0.5);
}

/** Согласны ли провайдеры между собой по действию (только по ответившим). */
export function agreement(vs: AIVerdict[]): "agree" | "disagree" | "single" {
  const ok = vs.filter((v) => v.status === "ok" && v.action);
  if (ok.length < 2) return "single";
  return ok.every((v) => v.action === ok[0]!.action) ? "agree" : "disagree";
}

/** Вероятность в процентах с прочерком. */
export function pct(p: number | undefined | null, digits = 1): string {
  if (p === undefined || p === null || !Number.isFinite(p)) return "—";
  return `${(p * 100).toFixed(digits)}%`;
}

/** Доля n/total в процентах с прочерком вместо деления на ноль. */
export function share(n: number, total: number): string {
  if (total <= 0) return "—";
  return `${((n / total) * 100).toFixed(0)}%`;
}

/** Деньги со знаком. */
export function money(raw: string | undefined | null): string {
  if (raw === undefined || raw === null || raw === "") return "—";
  const v = Number(raw);
  if (!Number.isFinite(v)) return "—";
  return `${v > 0 ? "+" : ""}${v.toFixed(2)}`;
}

/**
 * Обходит ли провайдер рынок по калибровке: Brier провайдера против Brier
 * середины стакана на тех же запросах. Меньше — лучше. null — сравнивать не по
 * чему (нет сведённых строк у одного из двух).
 */
export function beatsMarket(p: AIProviderSummary, s: AISummary): boolean | null {
  const market = s.baselines.find((b) => b.name === "market_mid");
  if (p.brier === undefined || market?.brier === undefined || p.settled === 0 || market.settled === 0) {
    return null;
  }
  return p.brier < market.brier;
}

/**
 * Порог выводов: столько сведённых ответов у КАЖДОГО провайдера, прежде чем
 * сравнение что-то значит (критерий приёмки docs/roadmap.md). До него панель
 * пишет «рано судить», а не победителя.
 */
export const AI_MIN_SETTLED = 200;

/** Готовы ли данные к выводам. */
export function enoughData(s: AISummary): boolean {
  return s.providers.length > 0 && s.providers.every((p) => p.settled >= AI_MIN_SETTLED);
}
